import {EditOutlined} from '@ant-design/icons/lib';
import {Button, Card, Typography} from 'antd';
import {
  AccountStatus,
  UpdateMasterAccountInput,
  useUpdateMasterAccountMutation
} from 'api/generated';
import {AnchorLink, Ellipsize, Hint, IBANInput, PropertyList, Stack} from 'components';
import {UrlInput} from 'components/Form/UrlInput';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select, SubmitButton, Switch} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {
  formatBoolean,
  formatEmail,
  formatIBAN,
  formatSignUpAnchorLink,
  formatSignUpLink,
  formatWebsite
} from 'lib/formaters';
import {accountStatusLabels} from 'lib/labels';
import {omitEmpty} from 'lib/omitEmpty';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

const {Option} = Select;
const {Text} = Typography;

export const MasterAccountDetails: React.FC<any> = ({cardProps, formProps, masterAccount}) => {
  const [isEditing, setEditing] = useState(false);
  const {isAdmin} = useAccessLevel();
  const [updateMasterAccount] = useUpdateMasterAccountMutation();

  if (!masterAccount) return <Card {...cardProps} title="Details" />;

  const initialValues: UpdateMasterAccountInput = {
    id: masterAccount.id,
    name: masterAccount.name,
    status: masterAccount.status,
    adminEmail: masterAccount.adminEmail,
    business: {
      legalName: masterAccount.business?.legalName || '',
      website: masterAccount.business?.website || '',
      documentNumber: masterAccount.business?.documentNumber || '',
      servicesDescription: masterAccount.business?.servicesDescription || ''
    },
    iban: masterAccount.iban,
    promoCode: masterAccount.promoCode,
    blocked: masterAccount.blocked
  };

  const handleSubmit = async (input: UpdateMasterAccountInput) => {
    await updateMasterAccount({
      variables: {
        input: omitEmpty(input)
      }
    });
    setEditing(false);
  };

  return (
    <Card
      {...cardProps}
      title="Details"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik onSubmit={handleSubmit} initialValues={initialValues}>
          <Form {...formProps}>
            <FormItem hasFeedback={false} name="name" label="Name">
              <Input name="name" placeholder="My name" />
            </FormItem>

            <FormItem hasFeedback={false} name="status" label="Status">
              <Select name="status">
                {Object.values(AccountStatus).map((status) => (
                  <Option key={status} value={status}>
                    {accountStatusLabels[status]}
                  </Option>
                ))}
              </Select>
            </FormItem>
            <FormItem
              name="blocked"
              label={
                <Text type="danger" strong>
                  Block transactions
                </Text>
              }>
              <Switch name="blocked" />
            </FormItem>
            <FormItem hasFeedback={false} name="adminEmail" label="Admin email">
              <Input name="adminEmail" type="email" placeholder="john@doe.com" />
            </FormItem>
            <FormItem hasFeedback={false} name="business.legalName" label="Legal name">
              <Input name="business.legalName" placeholder="My Company" />
            </FormItem>
            <FormItem hasFeedback={false} name="business.website" label="Website">
              <UrlInput name="business.website" placeholder="mycompany.com" />
            </FormItem>
            <FormItem
              hasFeedback={false}
              name="business.documentNumber"
              label="Tax Identification Number (CIF)">
              <Input name="business.documentNumber" placeholder="1234567889" />
            </FormItem>
            <FormItem
              hasFeedback={false}
              name="business.servicesDescription"
              label="Services Description">
              <Input.TextArea
                name="business.servicesDescription"
                placeholder="We sell shoes online in the e-commerce store."
              />{' '}
            </FormItem>
            <FormItem hasFeedback={false} name="promoCode" label="Promo code">
              <Input name="promoCode" />
            </FormItem>
            <FormItem hasFeedback={false} name="iban" label="IBAN">
              <IBANInput name="iban" countryCode={'ES'} />
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <PropertyList
          omitEmpty
          properties={[
            {
              label: 'ID',
              copyable: {text: masterAccount.id},
              value: (
                <Link to={`/payments?masterAccountId=${masterAccount.id}`}>{masterAccount.id}</Link>
              )
            },
            {
              label: 'Name',
              value: masterAccount.name
            },
            {
              label: 'Status',
              value: accountStatusLabels[masterAccount.status as AccountStatus]
            },
            {
              label: 'Blocked',
              value: formatBoolean(masterAccount.blocked)
            },
            {
              label: 'Admin email',
              value: masterAccount.adminEmail && (
                <Ellipsize maxWidth={{xl: '90ch', md: '25ch', sm: '15ch'}}>
                  {formatEmail(masterAccount.adminEmail)}
                </Ellipsize>
              )
            },
            {label: 'Legal name', value: masterAccount.business?.legalName || null},
            {label: 'Website', value: formatWebsite(masterAccount.business?.website || null)},
            {
              label: 'Tax Identification Number (CIF)',
              value: masterAccount.business?.documentNumber || null
            },
            {
              label: 'Services description',
              value: masterAccount.business?.servicesDescription || null
            },
            {
              label: 'Promo code',
              value: masterAccount.promoCode
            },
            {
              label: (
                <Hint
                  message={
                    <>
                      Share this link with your merchants to allow them to sign up with MONEI. More
                      information{' '}
                      <AnchorLink href="https://docs.monei.com/monei-connect/">here</AnchorLink>.
                    </>
                  }>
                  Sign up link
                </Hint>
              ),
              copyable: {text: formatSignUpLink(masterAccount.promoCode)},
              value: formatSignUpAnchorLink(masterAccount.promoCode)
            },
            {
              label: 'IBAN',
              value: formatIBAN(masterAccount.iban, true)
            }
          ]}
        />
      )}
    </Card>
  );
};
